/**
 * presentationStore.ts
 *
 * Zustand store for the presentation editor.  Holds all core state and slide-
 * mutation actions that were previously scattered across the 3 000-line
 * RoSContext.  External React-hook dependencies (router, auth, themes, etc.)
 * are injected by the RoSContext bridge via `setExternalDeps` so this file
 * stays free of React hooks.
 */

import { create } from 'zustand';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';

import type {
  Interaction,
  InteractionColors,
  InteractionOptions,
  InteractionSettings,
  InteractionType,
  PanelButton,
  Session,
  Template,
} from '@components/Presentation/types';
import type { Theme } from '@components/themes/store/ThemeStore';
import { db } from '../../../lib/db';
import { createPatch, type PatchData, type PresentationPatch } from '../../../lib/patches';
import { defaultInteractionConfig, panelBtnEnum, interactionTypeMap } from '../constants';
import { getDefaultTitle, notify, getDefaultOptions } from '../helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SaveState = 'saved' | 'saving' | 'unsaved';

type UndoRedoState = {
  slides: Interaction[];
  selectedIndex: number;
  themeId?: string;
  changeType: 'slide' | 'theme' | 'both';
  panelState: PanelButton | '';
};

type SlideHistoryHook = {
  sync: (idx: number, slide: Interaction, newType: InteractionType) => Interaction | undefined;
  remove: (index: number) => void;
  flush: () => void;
};

/** External hook data injected by the RoSContext bridge. */
export type ExternalDeps = {
  getGlobalSettings: (type: string) => Partial<InteractionSettings>;
  getGlobalColors: (type: string) => Partial<InteractionColors>;
  allThemes: Theme[];
  selectedTheme: Theme | undefined;
  slideHistory: SlideHistoryHook;
};

export interface PresentationState {
  // ── Core presentation state ──────────────────────────────────────────────
  slides: Interaction[];
  selectedSlideIndex: number;
  rosTitle: string;
  templateId: string | null;
  template: Template | null;
  session: Session | null;
  isEditable: boolean;
  layoutMode: 'square' | 'rectangle';
  saveState: SaveState;
  selectedThemeId: string | undefined;
  /** IDs of slides pending server-side deletion (kept in sync with IndexedDB). */
  deletedSlides: string[];

  // ── UI state (owned here because undo/redo restores it) ──────────────────
  selectedPanelBtn: PanelButton | '';
  canUndo: boolean;
  canRedo: boolean;

  // ── Multi-select ─────────────────────────────────────────────────────────
  selectedSlideIds: Record<string, string>;

  // ── Auto-save / network ──────────────────────────────────────────────────
  isAutoSaveEnabled: boolean;
  isOffline: boolean;
  retryCount: number;
  lastLocalSave: Date | null;
  patchQueue: PresentationPatch[];

  // ── Rate-limit flags ─────────────────────────────────────────────────────
  isPasting: boolean;
  isDuplicating: boolean;

  // ── Injected external deps (React-hook results, set by the bridge) ───────
  _ext: ExternalDeps | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  /** Called by the RoSContext bridge once on mount and whenever hook results change. */
  setExternalDeps: (deps: ExternalDeps) => void;

  // Presentation-level mutations
  setSlides: (slides: Interaction[] | ((prev: Interaction[]) => Interaction[])) => void;
  setSelectedSlideIndex: (index: number) => void;
  setRosTitle: (title: string) => void;
  updateRoSTitle: (value: string, markDirty?: () => void) => void;
  setTemplateId: (id: string | null) => void;
  setTemplate: (template: Template | null) => void;
  setSession: (session: Session | null) => void;
  setIsEditable: (editable: boolean) => void;
  setLayoutMode: (mode: 'square' | 'rectangle') => void;
  updateLayoutMode: (mode: 'square' | 'rectangle', markDirty?: () => void) => void;
  setSaveState: (state: SaveState) => void;
  setSelectedThemeId: (id: string | undefined) => void;
  setDeletedSlides: (slides: string[]) => void;

  // Panel / selections
  setSelectedPanelBtn: (btn: PanelButton | '') => void;
  toggleSelectedPanelBtn: (btn: PanelButton | '') => () => void;
  setSelectedSlideIds: (
    valueOrUpdater:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;

  // Auto-save flags
  setIsAutoSaveEnabled: (enabled: boolean) => void;
  setIsOffline: (offline: boolean) => void;
  setRetryCount: (count: number) => void;
  setLastLocalSave: (date: Date | null) => void;

  // Patch queue
  addPatch: (data: PatchData) => void;
  flushAndClearPatches: () => PresentationPatch[];
  setIsPasting: (v: boolean) => void;
  setIsDuplicating: (v: boolean) => void;

  // Undo / redo availability setters (called after save / undo / redo)
  setCanUndo: (v: boolean) => void;
  setCanRedo: (v: boolean) => void;

  // Undo / redo
  pushToHistory: (changeType?: 'slide' | 'theme' | 'both') => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;

  // Slide CRUD
  updateSelectedSlideIndex: (index: number) => void;
  switchSlide: (slide: Interaction, index: number) => () => void;
  addNewSlide: (interactionType: InteractionType, slide?: Interaction) => void;
  deleteSlide: (index: number, saveTemplateFn?: () => void) => void;
  batchDeleteSlides: (indices: number[], saveTemplateFn?: () => void) => void;
  duplicateSlide: () => void;
  changeInteraction: (value: string | number) => void;

  // Slide content mutations
  updateTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updateSetting: (key: keyof InteractionSettings, value: any) => void;
  updateQuizOptions: (index: number, checked: boolean) => void;
  updateColor: (key: keyof InteractionColors, value: string | boolean) => void;
  addOption: () => void;
  addBulkOptions: (count: number) => void;
  updateOptions: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
    choiceCircleOptions?: InteractionOptions[]
  ) => void;
  updateOptionsByName: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void;
  deleteOption: (index: number) => void;
  resetDesign: () => void;
  applyToSimilarSlides: () => void;
  onOptionChange: (options: InteractionOptions[]) => void;
  applySelectedTransitionToAllSlides: () => void;

  // Theme
  updateTheme: (theme: Theme) => void;

  // Drag & drop
  handleDnD: (oldIndex: number, newIndex: number) => void;
  handleMultiDnD: (sourceIndices: number[], targetIndex: number) => void;

  // Copy / paste
  onCopyInteraction: (index: number) => void;
  onPasteInteraction: (index: number) => Promise<void>;
  onCopyInteractionLink: (index: number) => void;
  generateEmbedLink: (index: number, teamOid: string) => void;

  // PowerPoint import
  addSlides: (file_name: string, powerPointSlides: PowerpointSlideInfo[]) => Promise<void>;

  // DeletedSlides sync to IndexedDB
  updateDeletedSlides: (slides: string[]) => Promise<void>;
}

// ---------------------------------------------------------------------------
// PowerPoint helper types (local)
// ---------------------------------------------------------------------------

type PowerpointSlideInfo = {
  description: string;
  header_text?: string;
  interaction: {
    title: string;
    type: InteractionType;
    options?: Array<string | { answer_choice: string; checked?: boolean }>;
  };
  slide_number: number;
};

// ---------------------------------------------------------------------------
// Debounce refs (module-level, not per-instance — fine for a singleton store)
// ---------------------------------------------------------------------------

let pushHistoryTimeout: ReturnType<typeof setTimeout> | null = null;
let undoRedoInProgress = false;
let undoRedoTimeout: ReturnType<typeof setTimeout> | null = null;
let titleChangeDelay: ReturnType<typeof setTimeout> | null = null;
let optionChangeDelay: ReturnType<typeof setTimeout> | null = null;
let duplicateLastTime = 0;
let pasteLastTime = 0;
const DUPLICATE_COOLDOWN = 2000;
const PASTE_COOLDOWN = 2000;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePresentationStore = create<PresentationState>()((set, get) => ({
  // ── Initial state ────────────────────────────────────────────────────────
  slides: [],
  selectedSlideIndex: -1,
  rosTitle: 'Untitled Presentation',
  templateId: null,
  template: null,
  session: null,
  isEditable: true,
  layoutMode: 'rectangle',
  saveState: 'saved',
  selectedThemeId: undefined,
  deletedSlides: [],
  selectedPanelBtn: '',
  canUndo: false,
  canRedo: false,
  selectedSlideIds: {},
  isAutoSaveEnabled: true,
  isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
  retryCount: 0,
  lastLocalSave: null,
  patchQueue: [],
  isPasting: false,
  isDuplicating: false,
  _ext: null,

  // ── External deps injection ───────────────────────────────────────────────
  setExternalDeps: (deps) => set({ _ext: deps }),

  // ── Simple setters ────────────────────────────────────────────────────────
  setSlides: (slidesOrUpdater) =>
    set((s) => ({
      slides:
        typeof slidesOrUpdater === 'function'
          ? slidesOrUpdater(s.slides)
          : slidesOrUpdater,
    })),
  setSelectedSlideIndex: (selectedSlideIndex) => set({ selectedSlideIndex }),
  setRosTitle: (rosTitle) => set({ rosTitle }),
  setTemplateId: (templateId) => set({ templateId }),
  setTemplate: (template) => set({ template }),
  setSession: (session) => set({ session }),
  setIsEditable: (isEditable) => set({ isEditable }),
  setLayoutMode: (layoutMode) => set({ layoutMode }),
  setSaveState: (saveState) => set({ saveState }),
  setSelectedThemeId: (selectedThemeId) => set({ selectedThemeId }),
  setDeletedSlides: (deletedSlides) => set({ deletedSlides }),
  setSelectedPanelBtn: (selectedPanelBtn) => set({ selectedPanelBtn }),
  setIsAutoSaveEnabled: (isAutoSaveEnabled) => set({ isAutoSaveEnabled }),
  setIsOffline: (isOffline) => set({ isOffline }),
  setRetryCount: (retryCount) => set({ retryCount }),
  setLastLocalSave: (lastLocalSave) => set({ lastLocalSave }),
  setIsPasting: (isPasting) => set({ isPasting }),
  setIsDuplicating: (isDuplicating) => set({ isDuplicating }),
  setCanUndo: (canUndo) => set({ canUndo }),
  setCanRedo: (canRedo) => set({ canRedo }),

  addPatch: (data) => {
    const patch = createPatch(data);
    set((s) => ({ patchQueue: [...s.patchQueue, patch] }));
  },

  flushAndClearPatches: () => {
    const { patchQueue, templateId } = get();
    set({ patchQueue: [] });
    if (templateId) {
      db.clearPatchesForPresentation(templateId).catch(() => {});
    }
    return patchQueue;
  },

  setSelectedSlideIds: (valueOrUpdater) =>
    set((s) => ({
      selectedSlideIds:
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(s.selectedSlideIds)
          : valueOrUpdater,
    })),

  toggleSelectedPanelBtn: (btn) => () => set({ selectedPanelBtn: btn }),

  updateRoSTitle: (value, markDirty) => {
    set({ rosTitle: value });
    get().addPatch({ rosTitle: value });
    markDirty?.();
  },

  updateLayoutMode: (mode, markDirty) => {
    set({ layoutMode: mode });
    get().addPatch({ layoutMode: mode });
    markDirty?.();
  },

  // ── DeletedSlides: keep ref in sync with IndexedDB ────────────────────────
  updateDeletedSlides: async (slides) => {
    const { templateId, rosTitle, layoutMode } = get();
    set({ deletedSlides: slides });
    if (!templateId) return;
    try {
      const backup = await db.getPresentation(templateId);
      if (backup) {
        await db.savePresentation({ ...backup, deletedSlides: slides });
      } else {
        await db.savePresentation({
          id: templateId,
          slides: [],
          rosTitle: rosTitle || 'Untitled Presentation',
          layoutMode,
          timestamp: new Date().toISOString(),
          version: '1.0',
          deletedSlides: slides,
        });
      }
    } catch (e) {
      console.error('Failed to update deleted slides in IndexedDB:', e);
    }
  },

  // ── Undo / redo ───────────────────────────────────────────────────────────
  pushToHistory: (changeType = 'slide') => {
    if (undoRedoInProgress) return;

    if (pushHistoryTimeout) {
      clearTimeout(pushHistoryTimeout);
    }

    const { slides, selectedSlideIndex, selectedThemeId, selectedPanelBtn } = get();

    pushHistoryTimeout = setTimeout(async () => {
      await db.pushHistory('undo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType,
        panelState: selectedPanelBtn,
      });
      set({ canUndo: true, canRedo: false });
      pushHistoryTimeout = null;
    }, 100);
  },

  undo: async () => {
    if (undoRedoInProgress) return;

    if (pushHistoryTimeout) {
      clearTimeout(pushHistoryTimeout);
      pushHistoryTimeout = null;
    }
    if (undoRedoTimeout) {
      clearTimeout(undoRedoTimeout);
    }

    undoRedoInProgress = true;

    try {
      const previousState = await db.getLatestHistory('undo');
      if (!previousState) {
        undoRedoInProgress = false;
        return;
      }

      await db.removeLatestHistory('undo');

      const { slides, selectedSlideIndex, selectedThemeId, selectedPanelBtn, _ext } =
        get();

      await db.pushHistory('redo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType: previousState.changeType,
        panelState: selectedPanelBtn,
      });

      if (previousState.changeType === 'theme') {
        if (previousState.themeId && previousState.themeId !== selectedThemeId) {
          const theme = _ext?.allThemes.find((t) => t.id === previousState.themeId);
          if (theme) get().updateTheme(theme);
        }
      } else if (previousState.changeType === 'slide') {
        if (previousState.slides.length <= slides.length) {
          set({
            slides: previousState.slides,
            selectedSlideIndex: previousState.selectedIndex,
          });
        }
      } else {
        if (previousState.slides.length <= slides.length) {
          set({
            slides: previousState.slides,
            selectedSlideIndex: previousState.selectedIndex,
          });
        }
        if (previousState.themeId && previousState.themeId !== selectedThemeId) {
          const theme = _ext?.allThemes.find((t) => t.id === previousState.themeId);
          if (theme) get().updateTheme(theme);
        }
      }

      set({ selectedSlideIds: {} });

      const hasMore = (await db.getLatestHistory('undo')) !== undefined;
      set({ canUndo: hasMore, canRedo: true });

      if (previousState.panelState !== undefined) {
        set({ selectedPanelBtn: previousState.panelState as PanelButton | '' });
      }
    } catch (err) {
      console.error('Error during undo:', err);
    }

    undoRedoTimeout = setTimeout(() => {
      undoRedoInProgress = false;
      undoRedoTimeout = null;
      set({ saveState: 'unsaved' });
      get().addPatch({ slides: get().slides });
    }, 300);
  },

  redo: async () => {
    if (undoRedoInProgress) return;

    if (pushHistoryTimeout) {
      clearTimeout(pushHistoryTimeout);
      pushHistoryTimeout = null;
    }
    if (undoRedoTimeout) {
      clearTimeout(undoRedoTimeout);
    }

    undoRedoInProgress = true;

    try {
      const nextState = await db.getLatestHistory('redo');
      if (!nextState) {
        undoRedoInProgress = false;
        return;
      }

      await db.removeLatestHistory('redo');

      const { slides, selectedSlideIndex, selectedThemeId, selectedPanelBtn, _ext } =
        get();

      await db.pushHistory('undo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType: nextState.changeType,
        panelState: selectedPanelBtn,
      });

      if (nextState.changeType === 'theme') {
        if (nextState.themeId && nextState.themeId !== selectedThemeId) {
          const theme = _ext?.allThemes.find((t) => t.id === nextState.themeId);
          if (theme) get().updateTheme(theme);
        }
      } else if (nextState.changeType === 'slide') {
        if (nextState.slides.length <= slides.length) {
          set({
            slides: nextState.slides,
            selectedSlideIndex: nextState.selectedIndex,
          });
        }
      } else {
        if (nextState.slides.length <= slides.length) {
          set({
            slides: nextState.slides,
            selectedSlideIndex: nextState.selectedIndex,
          });
        }
        if (nextState.themeId && nextState.themeId !== selectedThemeId) {
          const theme = _ext?.allThemes.find((t) => t.id === nextState.themeId);
          if (theme) get().updateTheme(theme);
        }
      }

      set({ selectedSlideIds: {} });
      set({ canUndo: true });
      const hasMore = (await db.getLatestHistory('redo')) !== undefined;
      set({ canRedo: hasMore });

      if (nextState.panelState !== undefined) {
        set({ selectedPanelBtn: nextState.panelState as PanelButton | '' });
      }
    } catch (err) {
      console.error('Error during redo:', err);
    }

    undoRedoTimeout = setTimeout(() => {
      undoRedoInProgress = false;
      undoRedoTimeout = null;
      set({ saveState: 'unsaved' });
      get().addPatch({ slides: get().slides });
    }, 300);
  },

  // ── Slide navigation ──────────────────────────────────────────────────────
  updateSelectedSlideIndex: (index) => {
    const { selectedSlideIndex } = get();
    if (selectedSlideIndex !== index) {
      get().pushToHistory();
      set({ selectedSlideIndex: index });
    }
  },

  switchSlide: (slide, index) => () => {
    set({
      selectedSlideIndex: index,
      selectedPanelBtn: slide?.type === 'PDF Slide' ? '' : panelBtnEnum.content,
    });
  },

  // ── Add new slide ─────────────────────────────────────────────────────────
  addNewSlide: (interactionType, slide) => {
    const { slides, selectedSlideIndex, _ext } = get();
    const getGlobalSettings = _ext?.getGlobalSettings ?? (() => ({}));
    const getGlobalColors = _ext?.getGlobalColors ?? (() => ({}));

    const options = getDefaultOptions(interactionType) as InteractionOptions[];
    const setting = getGlobalSettings(interactionType);
    const color = getGlobalColors(interactionType);

    const existingSlides = slides.filter(
      (s) => s.transition_type !== undefined && s.transition_type !== ''
    );
    const commonTransitionType =
      existingSlides.length > 0 &&
      existingSlides.every((s) => s.transition_type === existingSlides[0].transition_type)
        ? existingSlides[0].transition_type
        : 'none';

    if (slide) {
      slide.setting = { ...setting, ...(slide.setting || {}) };
      slide.color = { ...color, ...(slide.color || {}) };
      if (
        ['pollWithOptions', 'wheel', 'multipleChoice'].includes(interactionType) &&
        slide.options?.length < 2
      ) {
        slide.options = [...(slide.options || []), ...options];
      }
      if (interactionType === 'quiz') {
        if (!slide.options.some((o: InteractionOptions) => o.checked)) {
          slide.options[0].checked = true;
        }
      }
    }

    const newIndex = selectedSlideIndex + 1;
    set((s) => {
      const changed = [...s.slides];
      const defaultOptions = [...options] as InteractionOptions[];
      if (interactionType === 'quiz') defaultOptions[0].checked = true;

      const updatedSlide: Interaction = slide || {
        ...defaultInteractionConfig,
        title: getDefaultTitle(interactionType),
        setting: setting as InteractionSettings,
        color: color as InteractionColors,
        type: interactionType,
        options: defaultOptions,
        transition_type: commonTransitionType,
      };

      changed.splice(newIndex, 0, updatedSlide);
      return {
        slides: changed,
        selectedSlideIndex: newIndex,
        selectedPanelBtn: slide ? '' : panelBtnEnum.content,
        saveState: 'unsaved',
      };
    });
    get().addPatch({ slides: get().slides });
  },

  // ── Delete slide ──────────────────────────────────────────────────────────
  deleteSlide: (index, saveTemplateFn) => {
    const { slides, isEditable, deletedSlides, isAutoSaveEnabled } = get();
    if (slides.length === 1) {
      notify('Cannot delete all the slides. Please keep at least one slide.', 'error');
      return;
    }
    const slideToDelete = slides[index];
    if (slideToDelete?.id && isEditable && !deletedSlides.includes(slideToDelete.id)) {
      get().updateDeletedSlides([...deletedSlides, slideToDelete.id]);
    }

    const newSlides = slides.filter((_, i) => i !== index);
    const newIndex = newSlides.length === 0 ? -1 : Math.max(0, index - 1);

    set({
      slides: newSlides,
      selectedSlideIndex: newIndex,
      selectedPanelBtn: ['PDF Slide'].includes(newSlides[newIndex]?.type)
        ? ''
        : panelBtnEnum.content,
      saveState: 'unsaved',
    });
    get().addPatch({
      slides: get().slides,
      ...(slideToDelete?.id ? { deletedSlideIds: [slideToDelete.id] } : {}),
    });

    if (isAutoSaveEnabled && !undoRedoInProgress) {
      saveTemplateFn?.();
    }
  },

  // ── Batch delete ──────────────────────────────────────────────────────────
  batchDeleteSlides: (indices, saveTemplateFn) => {
    const { slides, isEditable, deletedSlides, isAutoSaveEnabled } = get();
    if (slides.length === indices.length) {
      notify('Cannot delete all the slides. Please keep at least one slide.', 'error');
      return;
    }

    const sortedIndices = [...indices].sort((a, b) => b - a);
    const newDeletedSlides = [...deletedSlides];
    sortedIndices.forEach((i) => {
      const s = slides[i];
      if (s?.id && isEditable && !newDeletedSlides.includes(s.id)) {
        newDeletedSlides.push(s.id);
      }
    });
    get().updateDeletedSlides(newDeletedSlides);

    const newSlides = slides.filter((_, i) => !indices.includes(i));
    const newIndex = Math.max(0, Math.min(...indices) - 1);
    const deletedIds = newDeletedSlides.filter((id) => !deletedSlides.includes(id));

    set({
      slides: newSlides,
      selectedSlideIndex: newIndex,
      selectedPanelBtn: ['PDF Slide'].includes(newSlides[newIndex]?.type)
        ? ''
        : panelBtnEnum.content,
      saveState: 'unsaved',
    });
    get().addPatch({
      slides: get().slides,
      ...(deletedIds.length > 0 ? { deletedSlideIds: deletedIds } : {}),
    });

    if (isAutoSaveEnabled && !undoRedoInProgress) {
      saveTemplateFn?.();
    }
  },

  // ── Duplicate slide ───────────────────────────────────────────────────────
  duplicateSlide: () => {
    const now = Date.now();
    const { isDuplicating, selectedSlideIds, slides, selectedSlideIndex } = get();

    if (isDuplicating || now - duplicateLastTime < DUPLICATE_COOLDOWN) {
      notify('Please wait a moment before duplicating again.', 'warning', {
        className: 'text-grey-900',
      });
      return;
    }
    duplicateLastTime = now;
    set({ isDuplicating: true });
    setTimeout(() => set({ isDuplicating: false }), DUPLICATE_COOLDOWN);

    const newSlides = [...slides];
    const selectedIds = Object.keys(selectedSlideIds);

    if (selectedIds.length > 0) {
      const selectedIndices = selectedIds
        .map((id) => newSlides.findIndex((s) => s.id === id))
        .sort((a, b) => a - b);
      if (!selectedIndices.length) return;

      const duplicates = selectedIndices.map((idx) => ({
        ...newSlides[idx],
        options: newSlides[idx]?.options?.map((o) => ({ ...o, id: undefined })) || [],
        id: undefined,
        link_id: undefined,
        title: newSlides[idx].title,
      }));

      const lastSelectedIndex = Math.max(...selectedIndices);
      newSlides.splice(lastSelectedIndex + 1, 0, ...duplicates);
      set({
        slides: newSlides,
        selectedSlideIndex: lastSelectedIndex + duplicates.length,
        selectedSlideIds: {},
        saveState: 'unsaved',
      });
      get().addPatch({ slides: get().slides });
    } else {
      const selected = newSlides[selectedSlideIndex];
      const newSlide = {
        ...selected,
        id: undefined,
        link_id: undefined,
        title: selected.title,
      };
      newSlides.splice(selectedSlideIndex + 1, 0, newSlide);
      set({
        slides: newSlides,
        selectedSlideIndex: selectedSlideIndex + 1,
        saveState: 'unsaved',
      });
      get().addPatch({ slides: get().slides });
    }
  },

  // ── Change interaction type ───────────────────────────────────────────────
  changeInteraction: (value) => {
    const {
      slides,
      selectedSlideIndex,
      selectedThemeId,
      _ext,
    } = get();

    get().pushToHistory();

    const getGlobalSettings = _ext?.getGlobalSettings ?? (() => ({}));
    const getGlobalColors = _ext?.getGlobalColors ?? (() => ({}));
    const allThemes = _ext?.allThemes ?? [];
    const slideHistory = _ext?.slideHistory;

    set((s) => {
      const prev = s.slides;
      const existingTitle = prev[selectedSlideIndex].title;
      const existingDefaultTitle = getDefaultTitle(prev[selectedSlideIndex].type as InteractionType);
      const wasCustomTitle = existingTitle !== existingDefaultTitle;

      const themeToApply = allThemes.find((t) => t.id === selectedThemeId);
      const themeColors = themeToApply?.config?.colors ?? {};
      const themeConfig = themeToApply?.config as any ?? {};

      let slide: Interaction = {
        ...defaultInteractionConfig,
        title: wasCustomTitle ? existingTitle : getDefaultTitle(value as InteractionType),
        setting: getGlobalSettings(value as InteractionType) as InteractionSettings,
        color: {
          ...getGlobalColors(value as InteractionType),
          ...themeColors,
        } as InteractionColors,
        transition_type: prev[selectedSlideIndex].transition_type || 'none',
      };

      // Apply theme-specific overrides (mirrors original logic)
      applyThemeSettingsForType(value as string, slide, themeConfig);

      slide.options = getDefaultOptions(value as InteractionType);

      if (slideHistory) {
        const existing = slideHistory.sync(
          selectedSlideIndex,
          prev[selectedSlideIndex],
          value as InteractionType
        );
        if (existing) slide = { ...existing };
      }

      const changed = [...prev];
      changed[selectedSlideIndex] = {
        ...slide,
        id: prev[selectedSlideIndex].id,
        type: value as InteractionType,
        title: wasCustomTitle ? existingTitle : slide.title,
      };

      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    get().addPatch({ slides: get().slides });
  },

  // ── Slide content mutations ───────────────────────────────────────────────
  updateTitle: (e) => {
    if (!e?.target) return;
    const { value } = e.target;
    if (value.includes('{"id":')) return;

    const { selectedSlideIndex } = get();
    set((s) => {
      if (selectedSlideIndex === null || selectedSlideIndex < 0) return s;
      const changed = [...s.slides];
      changed[selectedSlideIndex] = { ...changed[selectedSlideIndex], title: value };
      return { slides: changed };
    });

    if (titleChangeDelay) clearTimeout(titleChangeDelay);
    titleChangeDelay = setTimeout(() => {
      get().pushToHistory();
      set({ saveState: 'unsaved' });
      const slide = get().slides[selectedSlideIndex];
      if (slide?.id) {
        get().addPatch({ slidePatches: { [slide.id]: { title: value } } });
      } else {
        get().addPatch({ slides: get().slides });
      }
    }, 1000);
  },

  updateSetting: (key, value) => {
    get().pushToHistory();
    const { selectedSlideIndex } = get();
    set((s) => {
      const changed = [...s.slides];
      let data: any;
      switch (key) {
        case 'DisplayStyle':
          data = !value ? 'noOfVotes' : 'percentage';
          break;
        case 'transientThoughtDisplayStyle':
          data = value;
          break;
        case 'OptionsList':
          data = !value ? 'character' : 'number';
          break;
        default:
          data = value;
      }
      changed[selectedSlideIndex] = {
        ...s.slides[selectedSlideIndex],
        setting: { ...s.slides[selectedSlideIndex].setting, [key]: data },
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      const storedValue = updatedSlide.setting?.[key];
      get().addPatch({ slidePatches: { [updatedSlide.id]: { setting: { [key]: storedValue } } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  updateQuizOptions: (index, checked) => {
    const { selectedSlideIndex } = get();
    set((s) => {
      if (selectedSlideIndex === null || selectedSlideIndex < 0) return s;
      const changed = [...s.slides];
      const slide = changed[selectedSlideIndex];
      changed[selectedSlideIndex] = {
        ...slide,
        options: slide.options.map((o, i) => ({ ...o, checked: i === index ? checked : false })),
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      get().addPatch({ slidePatches: { [updatedSlide.id]: { options: updatedSlide.options } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  updateColor: (key, value) => {
    get().pushToHistory();
    const { selectedSlideIndex } = get();
    set((s) => {
      const changed = [...s.slides];
      changed[selectedSlideIndex] = {
        ...s.slides[selectedSlideIndex],
        color: { ...s.slides[selectedSlideIndex].color, [key]: value },
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      const storedValue = updatedSlide.color?.[key];
      get().addPatch({ slidePatches: { [updatedSlide.id]: { color: { [key]: storedValue } } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  addOption: () => {
    get().pushToHistory('slide');
    const { selectedSlideIndex } = get();
    set((s) => {
      const changed = [...s.slides];
      const slide = changed[selectedSlideIndex];
      const newIdx = slide.options.length;
      changed[selectedSlideIndex] = {
        ...slide,
        options: [...slide.options, { id: v4(), body: `Option ${newIdx + 1}` }],
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      get().addPatch({ slidePatches: { [updatedSlide.id]: { options: updatedSlide.options } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  addBulkOptions: (count) => {
    get().pushToHistory('slide');
    const { selectedSlideIndex } = get();
    set((s) => {
      const changed = [...s.slides];
      changed[selectedSlideIndex] = {
        ...changed[selectedSlideIndex],
        options: Array.from({ length: count }, (_, i) => ({
          id: v4(),
          body: (i + 1).toString(),
        })),
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      get().addPatch({ slidePatches: { [updatedSlide.id]: { options: updatedSlide.options } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  updateOptions: (index, e, choiceCircleOptions = []) => {
    if (!e) get().pushToHistory('slide');

    const { selectedSlideIndex } = get();
    set((s) => {
      if (selectedSlideIndex === null) return s;
      const changed = [...s.slides];
      const slide = changed[selectedSlideIndex];
      let newOptions: typeof slide.options;
      if (typeof index === 'number' && e?.target?.value !== undefined) {
        newOptions = slide.options.map((o, i) =>
          i === index ? { ...o, body: e.target.value } : o
        );
      } else if (Array.isArray(choiceCircleOptions)) {
        newOptions = choiceCircleOptions;
      } else {
        return s;
      }
      changed[selectedSlideIndex] = { ...slide, options: newOptions };
      return { slides: changed };
    });

    if (e) {
      if (optionChangeDelay) clearTimeout(optionChangeDelay);
      optionChangeDelay = setTimeout(() => {
        get().pushToHistory('slide');
        set({ saveState: 'unsaved' });
        const slide = get().slides[selectedSlideIndex];
        if (slide?.id) {
          get().addPatch({ slidePatches: { [slide.id]: { options: slide.options } } });
        } else {
          get().addPatch({ slides: get().slides });
        }
      }, 1000);
    } else {
      set({ saveState: 'unsaved' });
      const slide = get().slides[selectedSlideIndex];
      if (slide?.id) {
        get().addPatch({ slidePatches: { [slide.id]: { options: slide.options } } });
      } else {
        get().addPatch({ slides: get().slides });
      }
    }
  },

  updateOptionsByName: (index, e) => {
    if (e) {
      if (optionChangeDelay) clearTimeout(optionChangeDelay);
      optionChangeDelay = setTimeout(() => {
        get().pushToHistory('slide');
        set({ saveState: 'unsaved' });
        const slide = get().slides[get().selectedSlideIndex];
        if (slide?.id) {
          get().addPatch({ slidePatches: { [slide.id]: { options: slide.options } } });
        } else {
          get().addPatch({ slides: get().slides });
        }
      }, 1000);
    } else {
      get().pushToHistory();
      set({ saveState: 'unsaved' });
      const slide = get().slides[get().selectedSlideIndex];
      if (slide?.id) {
        get().addPatch({ slidePatches: { [slide.id]: { options: slide.options } } });
      } else {
        get().addPatch({ slides: get().slides });
      }
    }

    const { selectedSlideIndex } = get();
    set((s) => {
      if (selectedSlideIndex === null) return s;
      const changed = [...s.slides];
      const slide = changed[selectedSlideIndex];
      if (typeof index === 'number' && e?.target?.name && e?.target?.value !== undefined) {
        const option = cloneDeep(slide?.options[index]);
        if (!option) return s;
        const key = e.target.name as 'id' | 'body' | 'checked';
        if (key === 'id' || key === 'body' || key === 'checked') {
          (option as any)[key] = e.target.value;
        }
        changed[selectedSlideIndex] = {
          ...slide,
          options: [
            ...slide.options.slice(0, index),
            option,
            ...slide.options.slice(index + 1),
          ],
        };
      }
      return { slides: changed };
    });
  },

  deleteOption: (index) => {
    get().pushToHistory('slide');
    const { selectedSlideIndex } = get();
    set((s) => {
      if (selectedSlideIndex === null) return s;
      const changed = [...s.slides];
      const slide = changed[selectedSlideIndex];
      changed[selectedSlideIndex] = {
        ...slide,
        options: slide.options.filter((_, i) => i !== index),
      };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      get().addPatch({ slidePatches: { [updatedSlide.id]: { options: updatedSlide.options } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  resetDesign: () => {
    const { slides, selectedSlideIndex, selectedThemeId, _ext } = get();
    const getGlobalSettings = _ext?.getGlobalSettings ?? (() => ({}));
    const getGlobalColors = _ext?.getGlobalColors ?? (() => ({}));
    const allThemes = _ext?.allThemes ?? [];

    set((s) => {
      const changed = [...s.slides];
      const current = { ...changed[selectedSlideIndex] };
      const slideType = current.type;

      const color = {
        ...defaultInteractionConfig.color,
        ...getGlobalColors(slideType),
      } as InteractionColors;
      const defaultSettings = getGlobalSettings(slideType);
      const themeConfig =
        allThemes.find((t) => t.id === selectedThemeId)?.config;

      if (!themeConfig) {
        current.setting = {
          ...current.setting,
          FontStyle: (defaultSettings as any).FontStyle || 'Sans-Serif',
          HeadingFont: 'Sans-Serif',
        };
        changed[selectedSlideIndex] = current;
        return { slides: changed, saveState: 'unsaved' as SaveState };
      }

      const fontStyleMap: Partial<Record<InteractionType, string>> = {
        pollWithOptions: 'pollWithOptionsFontStyle',
        freeForm: 'pollFontStyle',
        wordCloud: 'wordCloudFontStyle',
        textTrack: 'textTrackFontStyle',
        transientThought: 'transientThoughtFontStyle',
        fantasticFans: 'fantasticFansFontStyle',
        multipleChoice: 'multipleChoiceFontStyle',
        credits: 'creditsFontStyle',
      };
      const headingFontMap: Partial<Record<InteractionType, string>> = {
        pollWithOptions: 'pollWithOptionsHeadingFont',
        freeForm: 'pollHeadingFont',
        wordCloud: 'wordCloudHeadingFont',
        map: 'mapHeadingFont',
        emojisEverywhere: 'emojisEverywhereHeadingFont',
        talkingTiles: 'talkingTilesHeadingFont',
        transientThought: 'transientThoughtHeadingFont',
        textTrack: 'textTrackHeadingFont',
        newWheel: 'newWheelHeadingFont',
        wheel: 'wheelHeadingFont',
        linkLibrary: 'linkLibraryHeadingFont',
        'Quick Questions': 'quickQuestionsHeadingFont',
        fantasticFans: 'fantasticFansHeadingFont',
        ratingPolls: 'ratingPollsHeadingFont',
        multipleChoice: 'multipleChoiceHeadingFont',
        credits: 'creditsHeadingFont',
      };

      current.color = color;

      const tc = themeConfig as any;
      const fKey = fontStyleMap[slideType];
      const fontStyle = fKey && tc?.textFontFamily?.[fKey]
        ? tc.textFontFamily[fKey]
        : tc?.default?.headingFontFamily || 'Sans-Serif';

      const hKey = headingFontMap[slideType];
      const headingFont = hKey && tc?.headingFontFamily?.[hKey]
        ? tc.headingFontFamily[hKey]
        : tc?.default?.headingFontFamily || 'Sans-Serif';

      current.setting = {
        ...current.setting,
        FontStyle: fontStyle,
        HeadingFont: headingFont,
        ...getImageResetSettings(slideType, defaultSettings as any),
      };

      changed[selectedSlideIndex] = current;
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });

    get().pushToHistory();
    const resetSlide = get().slides[selectedSlideIndex];
    if (resetSlide?.id) {
      get().addPatch({ slidePatches: { [resetSlide.id]: { color: resetSlide.color, setting: resetSlide.setting } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
    notify('Design reset to default');
  },

  applyToSimilarSlides: () => {
    get().pushToHistory('slide');
    const { slides, selectedSlideIndex } = get();
    const current = slides[selectedSlideIndex];

    set((s) => ({
      slides: s.slides.map((slide) => {
        if (slide.type !== current.type) return slide;
        return {
          ...slide,
          color: { ...current.color },
          setting: { ...slide.setting, FontStyle: current.setting.FontStyle },
        };
      }),
      saveState: 'unsaved' as SaveState,
    }));
    get().addPatch({ slides: get().slides });

    const nameMap: Record<string, string> = {
      'PDF Slide': 'PDF Slide',
      wheel: 'choice circle',
      pollWithOptions: 'power polls',
      freeForm: 'open-ended polls',
      wordCloud: 'wonder words',
      map: 'magic maps',
      emojisEverywhere: 'emojis everywhere',
      talkingTiles: 'talking tiles',
      transientThought: 'transient thoughts',
      'Quick Questions': 'quick questions',
      linkLibrary: 'link library',
      textTrack: 'text track',
      newWheel: 'winning wheel',
      ratingPolls: 'rating polls',
      fantasticFans: 'fantastic fans',
      multipleChoice: 'multiple choice',
      quiz: 'quiz',
      leaderboard: 'leaderboard',
    };
    notify(
      `Your design changes have been successfully applied to all ${nameMap[current.type] ?? current.type}.`
    );
  },

  onOptionChange: (options) => {
    get().pushToHistory('slide');
    const { selectedSlideIndex } = get();
    set((s) => {
      const changed = [...s.slides];
      changed[selectedSlideIndex] = { ...changed[selectedSlideIndex], options };
      return { slides: changed, saveState: 'unsaved' as SaveState };
    });
    const updatedSlide = get().slides[selectedSlideIndex];
    if (updatedSlide?.id) {
      get().addPatch({ slidePatches: { [updatedSlide.id]: { options } } });
    } else {
      get().addPatch({ slides: get().slides });
    }
  },

  applySelectedTransitionToAllSlides: () => {
    get().pushToHistory('slide');
    const { slides, selectedSlideIndex, templateId } = get();
    const selectedTransition = slides[selectedSlideIndex]?.transition_type || '';

    set((s) => {
      const updated = s.slides.map((slide) => ({
        ...slide,
        transition_type: selectedTransition,
      }));

      if (templateId) {
        db.getPresentation(templateId)
          .then((backup) => {
            if (backup) {
              db.savePresentation({
                ...backup,
                slides: updated,
                timestamp: new Date().toISOString(),
              });
            }
          })
          .catch((e) => console.error('Failed to save transition changes:', e));
      }

      return { slides: updated, saveState: 'unsaved' as SaveState };
    });
    get().addPatch({ slides: get().slides });
  },

  // ── Theme ─────────────────────────────────────────────────────────────────
  updateTheme: (theme) => {
    get().pushToHistory('theme');
    set((s) => ({
      selectedThemeId: theme.id,
      slides: s.slides.map((slide) => {
        if (slide?.type === 'PDF Slide') return slide;
        return applyThemeToSlide(slide, theme);
      }),
      saveState: 'unsaved' as SaveState,
    }));
    get().addPatch({ slides: get().slides });
  },

  // ── Drag & drop ───────────────────────────────────────────────────────────
  handleDnD: (oldIndex, newIndex) => {
    get().pushToHistory('slide');
    set((s) => {
      const newSlides = [...s.slides];
      const [movedSlide] = newSlides.splice(oldIndex, 1);
      newSlides.splice(newIndex, 0, movedSlide);
      return {
        slides: newSlides,
        selectedSlideIndex: newIndex,
        selectedPanelBtn: movedSlide.type === 'PDF Slide' ? '' : 'Content',
        saveState: 'unsaved' as SaveState,
      };
    });
    get().addPatch({ slides: get().slides });
  },

  handleMultiDnD: (sourceIndices, targetIndex) => {
    get().pushToHistory('slide');
    set((s) => {
      const newSlides = [...s.slides];
      const extendedIndices = Array.from(new Set(sourceIndices)).sort((a, b) => a - b);
      const selectedSlides = extendedIndices.map((i) => newSlides[i]);
      let adjustedTarget = targetIndex;

      extendedIndices.sort((a, b) => b - a).forEach((idx) => {
        newSlides.splice(idx, 1);
        if (idx < adjustedTarget) adjustedTarget--;
      });

      newSlides.splice(adjustedTarget, 0, ...selectedSlides);

      return {
        slides: newSlides,
        selectedSlideIndex: adjustedTarget,
        selectedPanelBtn: selectedSlides[0].type === 'PDF Slide' ? '' : 'Content',
        saveState: 'unsaved' as SaveState,
      };
    });
    get().addPatch({ slides: get().slides });
  },

  // ── Copy / paste ──────────────────────────────────────────────────────────
  onCopyInteraction: (index) => {
    const { slides, selectedSlideIds, selectedSlideIndex } = get();
    const selectedIds = Object.keys(selectedSlideIds);
    if (selectedIds.length > 0) {
      const selected = selectedIds
        .map((id) => slides.find((s) => s.id === id))
        .filter(Boolean);
      navigator.clipboard.writeText(JSON.stringify(selected));
      return;
    }

    const question = slides[index];
    if (!question) return;

    if (question.original_question_id) {
      set((s) => {
        const changed = [...s.slides];
        changed[selectedSlideIndex] = {
          ...changed[selectedSlideIndex],
          original_question_id: null,
        };
        return { slides: changed };
      });
    }
    navigator.clipboard.writeText(JSON.stringify(question));
  },

  onPasteInteraction: async (index) => {
    const now = Date.now();
    const { isPasting } = get();
    if (isPasting || now - pasteLastTime < PASTE_COOLDOWN) {
      notify('Please wait a moment before pasting again.', 'warning', {
        className: 'text-grey-900',
      });
      return;
    }
    pasteLastTime = now;
    set({ isPasting: true });

    try {
      const clipText = await navigator.clipboard.readText();
      const clipData = JSON.parse(clipText);

      if (Array.isArray(clipData)) {
        const flat = clipData.flat();
        const items: Interaction[] = flat.map((item) => {
          const clean = cloneDeep(item);
          delete clean.id;
          delete clean.link_id;
          delete clean.original_question_id;
          if (!clean.color) clean.color = item.color || defaultInteractionConfig.color;
          if (!clean.transition_type) clean.transition_type = 'none';
          return clean;
        });

        set((s) => {
          const newSlides = [
            ...s.slides.slice(0, index + 1),
            ...items,
            ...s.slides.slice(index + 1),
          ];
          const lastPasted = index + items.length;
          return {
            slides: newSlides,
            selectedSlideIndex: lastPasted,
            selectedPanelBtn: ['PDF Slide'].includes(items[0]?.type) ? '' : panelBtnEnum.content,
            saveState: 'unsaved' as SaveState,
          };
        });
        get().addPatch({ slides: get().slides });
      } else {
        const item = cloneDeep(clipData);
        delete item.id;
        delete item.link_id;
        delete item.original_question_id;
        if (!item.transition_type) item.transition_type = 'none';

        set((s) => ({
          slides: [
            ...s.slides.slice(0, index + 1),
            item,
            ...s.slides.slice(index + 1),
          ],
          selectedSlideIndex: index + 1,
          selectedPanelBtn: ['PDF Slide'].includes(item.type) ? '' : panelBtnEnum.content,
          saveState: 'unsaved' as SaveState,
        }));
        get().addPatch({ slides: get().slides });
      }
    } catch (err) {
      console.error('Failed to paste interaction:', err);
      notify('Failed to paste interaction from clipboard.', 'error');
    } finally {
      set({ isPasting: false });
    }
  },

  onCopyInteractionLink: (index) => {
    const { slides, selectedSlideIndex, layoutMode } = get();
    const question = slides[index];

    if (question.original_question_id) {
      set((s) => {
        const changed = [...s.slides];
        changed[selectedSlideIndex] = {
          ...changed[selectedSlideIndex],
          original_question_id: null,
        };
        return { slides: changed };
      });
    }

    const link_id = question.link_id;
    if (!link_id) {
      notify('Link not found.', 'error');
      return;
    }
    const fullPath = `${process.env.NEXT_PUBLIC_MAIN_APP_URL}/l1/${link_id}?layoutMode=${layoutMode}`;
    navigator.clipboard.writeText(fullPath);
    notify('Link copied to clipboard.');
  },

  generateEmbedLink: (index, teamOid) => {
    const { slides, templateId } = get();
    const question = slides[index];
    const now = Date.now();
    const link = `${process.env.NEXT_PUBLIC_APP_URL}/connect-to-stream?templateId=${templateId}&selectedSlideId=${question.id}&userDetailsId=${teamOid}&isEDU=true&t=${now}`;
    navigator.clipboard.writeText(link);
    notify('Link copied to clipboard.');
  },

  // ── PowerPoint import ─────────────────────────────────────────────────────
  addSlides: (file_name, powerPointSlides) => {
    const { slides, selectedSlideIndex, _ext, rosTitle } = get();
    const getGlobalSettings = _ext?.getGlobalSettings ?? (() => ({}));
    const getGlobalColors = _ext?.getGlobalColors ?? (() => ({}));

    const sorted = [...powerPointSlides].sort((a, b) => {
      const isSpecial = (s: PowerpointSlideInfo) =>
        s.slide_number === 0 &&
        ['Winning Wheel', 'Choice Circle', 'Quick Questions'].includes(s.interaction.type);
      const aS = isSpecial(a);
      const bS = isSpecial(b);
      if (aS && !bS) return 1;
      if (!aS && bS) return -1;
      return a.slide_number - b.slide_number;
    });

    const slidesWithTransition = slides.filter(
      (s) => s.transition_type !== undefined && s.transition_type !== ''
    );
    const isUniform =
      slidesWithTransition.length > 0 &&
      slidesWithTransition.every(
        (s) => s.transition_type === slidesWithTransition[0].transition_type
      );
    const commonTransition = isUniform ? slidesWithTransition[0].transition_type! : 'none';

    return new Promise<void>((resolve) => {
      const newSlides: Interaction[] = sorted.map((slideInfo) => {
        const interactionType =
          interactionTypeMap[slideInfo.interaction.type] || 'freeForm';
        const setting = getGlobalSettings(interactionType);
        const color = getGlobalColors(interactionType);
        const options = prepareInteractionOptions(slideInfo, interactionType);

        return {
          title: slideInfo.interaction.title,
          setting: setting as InteractionSettings,
          color: color as InteractionColors,
          type: interactionType as InteractionType,
          options,
          transition_type: commonTransition,
          ppt_slide_info: {
            description: slideInfo.description ?? '',
            slide_number: slideInfo.slide_number,
            header_text: slideInfo.header_text ?? '',
          },
        } satisfies Interaction;
      });

      const insertStart = selectedSlideIndex + 1;
      set((s) => {
        const updated = [...s.slides];
        updated.splice(insertStart, 0, ...newSlides);
        return { slides: updated, saveState: 'unsaved' as SaveState };
      });
      get().addPatch({ slides: get().slides });

      if (!rosTitle || rosTitle.includes('Untitled')) {
        get().updateRoSTitle(file_name);
      }
      get().updateSelectedSlideIndex(insertStart);
      set({ selectedPanelBtn: panelBtnEnum.content });
      resolve();
    });
  },
}));

// ---------------------------------------------------------------------------
// Helpers (module-level, no React)
// ---------------------------------------------------------------------------

function prepareInteractionOptions(
  slideInfo: PowerpointSlideInfo,
  interactionType: string
): InteractionOptions[] {
  const needsMin = ['pollWithOptions', 'wheel', 'multipleChoice'];
  const defaultOpts = getDefaultOptions(interactionType as InteractionType);
  let base: InteractionOptions[] = [];

  if (Array.isArray(slideInfo.interaction?.options)) {
    if (interactionType === 'multipleChoice') {
      base = (slideInfo.interaction.options as any[]).map((o: any, i: number) => ({
        id: v4(),
        option_number: i + 1,
        body: o.answer_choice,
        checked: o.checked ?? false,
      }));
    } else {
      base = (slideInfo.interaction.options as any[]).map((o: any, i: number) => ({
        id: v4(),
        option_number: i + 1,
        body: o,
        checked: false,
      }));
    }
  }

  if (needsMin.includes(interactionType)) {
    const needed = 2 - base.length;
    return needed > 0 ? [...base, ...defaultOpts.slice(0, needed)] : base;
  }
  return base;
}

/** Apply theme font / image settings to a single slide (mirrors updateTheme logic). */
function applyThemeToSlide(slide: Interaction, theme: Theme): Interaction {
  const tc = theme.config as any;
  const colors = { ...tc.colors };
  let FontStyle = slide.setting.FontStyle;
  const newSetting = { ...slide.setting };

  if (slide.type === 'pollWithOptions') {
    FontStyle = tc.textFontFamily?.pollWithOptionsFontStyle;
    Object.assign(newSetting, {
      pollWithOptionsImageSrc: tc.images?.pollWithOptionsImageSrc,
      pollWithOptionsImageOpacity: tc.images?.pollWithOptionsImageOpacity,
      HeadingFont: tc.headingFontFamily?.pollWithOptionsHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      pollWithOptionsFontSize: tc.fontSize,
    });
  } else if (slide.type === 'freeForm') {
    FontStyle = tc.textFontFamily?.pollFontStyle || tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      pollImageSrc: tc.images?.pollImageSrc || tc.default?.imageSrc,
      pollImageOpacity: tc.images?.pollImageOpacity || tc.default?.imageOpacity,
      HeadingFont: tc.headingFontFamily?.pollHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      pollFontSize: tc.fontSize,
    });
  } else if (slide.type === 'fantasticFans') {
    FontStyle = tc.textFontFamily?.fantasticFansFontStyle;
    Object.assign(newSetting, {
      ffImageSrc: tc.images?.ffImageSrc,
      ffImageOpacity: tc.images?.ffImageOpacity,
      HeadingFont: tc.headingFontFamily?.fantasticFansHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      ffFontSize: tc.fontSizes?.ffFontSize ?? 1.2,
    });
  } else if (slide.type === 'textTrack') {
    FontStyle = tc.textFontFamily?.textTrackFontStyle;
    Object.assign(newSetting, {
      textTrackImageSrc: tc.images?.textTrackImageSrc,
      textTrackImageOpacity: tc.images?.textTrackImageOpacity,
      HeadingFont: tc.headingFontFamily?.textTrackHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      textTrackFontSize: tc.fontSize,
    });
  } else if (slide.type === 'transientThought') {
    FontStyle = tc.textFontFamily?.transientThoughtFontStyle || tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      ttImageSrc: tc.images?.ttImageSrc,
      ttImageOpacity: tc.images?.ttImageOpacity,
      HeadingFont: tc.headingFontFamily?.transientThoughtHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      transientThoughtDisplayStyle: tc.settings?.transientThoughtDisplayStyle,
    });
  } else if (slide.type === 'wordCloud') {
    FontStyle = tc.textFontFamily?.wordCloudFontStyle;
    Object.assign(newSetting, {
      wcImageSrc: tc.images?.wcImageSrc,
      wcImageOpacity: tc.images?.wcImageOpacity,
      HeadingFont: tc.headingFontFamily?.wordCloudHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      wordCloudDisplayStyle: tc.settings?.wordCloudDisplayStyle,
      wordCloudSpread: tc.settings?.wordCloudSpread,
    });
  } else if (slide.type === 'emojisEverywhere') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.emojisEverywhereHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      emojisEverywhereImageSrc: tc.images?.emojisEverywhereImageSrc,
      emojisEverywhereImageOpacity: tc.images?.emojisEverywhereImageOpacity,
    });
  } else if (slide.type === 'map') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.mapHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      mapImageSrc: tc.images?.mapImageSrc,
      mapImageOpacity: tc.images?.mapImageOpacity,
      mapConfig: tc.settings?.mapConfig,
    });
  } else if (slide.type === 'wheel') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.wheelHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      choiceCircleImageSrc: tc.images?.choiceCircleImageSrc,
      choiceCircleImageOpacity: tc.images?.choiceCircleImageOpacity,
      choiceCircleFontSize: tc.fontSize,
    });
  } else if (slide.type === 'talkingTiles') {
    FontStyle = tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.talkingTilesHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      talkingTilesImageSrc: tc.images?.talkingTilesImageSrc,
      talkingTilesImageOpacity: tc.images?.talkingTilesImageOpacity,
      talkingTilesFontSize: tc.fontSize,
    });
  } else if (slide.type === 'newWheel') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.newWheelHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      winningWheelImageSrc: tc.images?.winningWheelImageSrc,
      winningWheelImageOpacity: tc.images?.winningWheelImageOpacity,
      winningWheelFontSize: tc.fontSize,
    });
  } else if (slide.type === 'linkLibrary') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.linkLibraryHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      linkLibraryImageSrc: tc.images?.linkLibraryImageSrc,
      linkLibraryImageOpacity: tc.images?.linkLibraryImageOpacity,
      linkLibraryFontSize: tc.fontSize,
    });
  } else if (slide.type === 'Quick Questions') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.quickQuestionsHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      quickQuestionImageSrc: tc.images?.quickQuestionImageSrc,
      quickQuestionImageOpacity: tc.images?.quickQuestionImageOpacity,
      quickQuestionFontSize: tc.fontSize,
    });
  } else if (slide.type === 'ratingPolls') {
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.ratingPollsHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      ratingPollsImageSrc: tc.images?.ratingPollsImageSrc,
      ratingPollsImageOpacity: tc.images?.ratingPollsImageOpacity,
      ratingPollsFontSize: tc.fontSize,
    });
    if (!colors.ratingPollsBgColor) colors.ratingPollsBgColor = colors.pollBgColor || tc.default?.bgColor || '#111111';
    if (!colors.ratingPollsChartColor) colors.ratingPollsChartColor = colors.pollChartColor || '#F97B5F';
    if (!colors.ratingPollsHeadingColor) colors.ratingPollsHeadingColor = colors.pollHeadingColor || tc.default?.headingColor || '#FFFFFF';
    if (!colors.ratingPollsLabelColor) colors.ratingPollsLabelColor = colors.pollLabelColor || '#FFFFFF';
    if (!colors.ratingPollsTextColor) colors.ratingPollsTextColor = colors.pollLabelColor || '#FFFFFF';
  } else if (slide.type === 'multipleChoice') {
    FontStyle = tc.textFontFamily?.multipleChoiceFontStyle || tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.multipleChoiceHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      multipleChoiceImageSrc: tc.images?.multipleChoiceImageSrc || tc.default?.imageSrc,
      multipleChoiceImageOpacity: tc.images?.multipleChoiceImageOpacity || tc.default?.imageOpacity,
      multipleChoiceFontSize: tc.fontSize,
    });
  } else if (slide.type === 'quiz') {
    FontStyle = tc.textFontFamily?.quizFontStyle || tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.quizHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      quizImageSrc: tc.images?.quizImageSrc || tc.default?.imageSrc,
      quizImageOpacity: tc.images?.quizImageOpacity || tc.default?.imageOpacity,
      quizFontSize: tc.fontSize,
    });
    if (!colors.quizBgColor) colors.quizBgColor = colors.pollBgColor || tc.default?.bgColor || '#111111';
    if (!colors.quizChartColor) colors.quizChartColor = colors.pollChartColor || '#F97B5F';
    if (!colors.quizHeadingColor) colors.quizHeadingColor = colors.pollHeadingColor || '#FFFFFF';
    if (!colors.quizLabelColor) colors.quizLabelColor = colors.pollLabelColor || '#FFFFFF';
  } else if (slide.type === 'leaderboard') {
    FontStyle = tc.textFontFamily?.leaderboardFontStyle || tc.default?.headingFontFamily || 'Sans-Serif';
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.leaderboardHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      leaderboardImageSrc: tc.images?.leaderboardImageSrc || tc.default?.imageSrc,
      leaderboardImageOpacity: tc.images?.leaderboardImageOpacity || tc.default?.imageOpacity,
      leaderboardFontSize: tc.fontSizes?.leaderboardFontSize ?? 1.2,
    });
    if (!colors.leaderboardBgColor) colors.leaderboardBgColor = colors.quizBgColor || colors.pollBgColor || tc.default?.bgColor || '#111111';
    if (!colors.leaderboardChartColor) colors.leaderboardChartColor = colors.quizChartColor || colors.pollChartColor || '#F97B5F';
    if (!colors.leaderboardHeadingColor) colors.leaderboardHeadingColor = colors.quizHeadingColor || colors.pollHeadingColor || '#FFFFFF';
    if (!colors.leaderboardLabelColor) colors.leaderboardLabelColor = colors.quizLabelColor || colors.pollLabelColor || '#FFFFFF';
  } else if (slide.type === 'credits') {
    FontStyle = tc.textFontFamily?.creditsFontStyle || tc.textFontFamily?.fantasticFansFontStyle;
    Object.assign(newSetting, {
      HeadingFont: tc.headingFontFamily?.creditsHeadingFont || tc.headingFontFamily?.fantasticFansHeadingFont || tc.default?.headingFontFamily || 'Sans-Serif',
      creditsImageSrc: tc.images?.creditsImageSrc || tc.images?.ffImageSrc,
      creditsImageOpacity: tc.images?.creditsImageSrc
        ? tc.images?.creditsImageOpacity
        : tc.images?.ffImageOpacity,
      creditsFontSize: tc.fontSizes?.creditsFontSize ?? tc.fontSizes?.ffFontSize ?? 1,
    });
    if (!colors.creditsBgColor) colors.creditsBgColor = colors.ffBgColor || tc.default?.bgColor || '#111111';
    if (!colors.creditsTextColor) colors.creditsTextColor = colors.ffTextColor || tc.default?.headingColor || '#FFFFFF';
    if (!colors.creditsHeadingColor) colors.creditsHeadingColor = colors.ffHeadingColor || tc.default?.headingColor || '#FFFFFF';
  }

  return {
    ...slide,
    color: colors,
    setting: { ...newSetting, FontStyle },
  };
}

/** Return the image-related setting resets for resetDesign. */
function getImageResetSettings(
  slideType: InteractionType,
  defaultSettings: any
): Partial<InteractionSettings> {
  const map: Record<string, (d: any) => Partial<InteractionSettings>> = {
    pollWithOptions: (d) => ({ pollWithOptionsImageSrc: d.pollWithOptionsImageSrc, pollWithOptionsImageOpacity: d.pollWithOptionsImageOpacity }),
    freeForm: (d) => ({ pollImageSrc: d.pollImageSrc, pollImageOpacity: d.pollImageOpacity }),
    wordCloud: (d) => ({ wcImageSrc: d.wcImageSrc, wcImageOpacity: d.wcImageOpacity }),
    map: (d) => ({ mapImageSrc: d.mapImageSrc, mapImageOpacity: d.mapImageOpacity, mapConfig: d.mapConfig }),
    emojisEverywhere: (d) => ({ emojisEverywhereImageSrc: d.emojisEverywhereImageSrc, emojisEverywhereImageOpacity: d.emojisEverywhereImageOpacity }),
    talkingTiles: (d) => ({ talkingTilesImageSrc: d.talkingTilesImageSrc, talkingTilesImageOpacity: d.talkingTilesImageOpacity }),
    transientThought: (d) => ({ ttImageSrc: d.ttImageSrc, ttImageOpacity: d.ttImageOpacity }),
    textTrack: (d) => ({ textTrackImageSrc: d.textTrackImageSrc, textTrackImageOpacity: d.textTrackImageOpacity }),
    newWheel: (d) => ({ winningWheelImageSrc: d.winningWheelImageSrc, winningWheelImageOpacity: d.winningWheelImageOpacity }),
    linkLibrary: (d) => ({ linkLibraryImageSrc: d.linkLibraryImageSrc, linkLibraryImageOpacity: d.linkLibraryImageOpacity }),
    'Quick Questions': (d) => ({ quickQuestionImageSrc: d.quickQuestionImageSrc, quickQuestionImageOpacity: d.quickQuestionImageOpacity }),
    ratingPolls: (d) => ({ ratingPollsImageSrc: d.ratingPollsImageSrc, ratingPollsImageOpacity: d.ratingPollsImageOpacity }),
    multipleChoice: (d) => ({ multipleChoiceImageSrc: d.multipleChoiceImageSrc, multipleChoiceImageOpacity: d.multipleChoiceImageOpacity }),
    credits: (d) => ({ creditsImageSrc: d.creditsImageSrc, creditsImageOpacity: d.creditsImageOpacity }),
  };
  return map[slideType]?.(defaultSettings) ?? {};
}

/** Apply theme settings for a NEW slide being created (changeInteraction). */
function applyThemeSettingsForType(
  value: string,
  slide: Interaction,
  themeConfig: any
): void {
  if (value === 'pollWithOptions') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.pollWithOptionsFontStyle;
    slide.setting.pollWithOptionsImageSrc = themeConfig.images?.pollWithOptionsImageSrc;
    slide.setting.pollWithOptionsImageOpacity = themeConfig.images?.pollWithOptionsImageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.pollWithOptionsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
  } else if (value === 'freeForm') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.pollFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.pollImageSrc = themeConfig.images?.pollImageSrc || themeConfig.default?.imageSrc;
    slide.setting.pollImageOpacity = themeConfig.images?.pollImageOpacity || themeConfig.default?.imageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.pollHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
  } else if (value === 'fantasticFans') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.fantasticFansFontStyle;
    slide.setting.ffImageSrc = themeConfig.images?.ffImageSrc;
    slide.setting.ffImageOpacity = themeConfig.images?.ffImageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.fantasticFansHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
  } else if (value === 'textTrack') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.textTrackFontStyle;
    slide.setting.textTrackImageSrc = themeConfig.images?.textTrackImageSrc;
    slide.setting.textTrackImageOpacity = themeConfig.images?.textTrackImageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.textTrackHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
  } else if (value === 'transientThought') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.transientThoughtFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.ttImageSrc = themeConfig.images?.ttImageSrc;
    slide.setting.ttImageOpacity = themeConfig.images?.ttImageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.transientThoughtHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.transientThoughtDisplayStyle = themeConfig.settings?.transientThoughtDisplayStyle;
  } else if (value === 'wordCloud') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.wordCloudFontStyle;
    slide.setting.wcImageSrc = themeConfig.images?.wcImageSrc;
    slide.setting.wcImageOpacity = themeConfig.images?.wcImageOpacity;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.wordCloudHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.wordCloudDisplayStyle = themeConfig.settings?.wordCloudDisplayStyle;
    slide.setting.wordCloudSpread = themeConfig.settings?.wordCloudSpread;
  } else if (value === 'emojisEverywhere') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.emojisEverywhereHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.emojisEverywhereImageSrc = themeConfig.images?.emojisEverywhereImageSrc;
    slide.setting.emojisEverywhereImageOpacity = themeConfig.images?.emojisEverywhereImageOpacity;
  } else if (value === 'map') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.mapHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.mapImageSrc = themeConfig.images?.mapImageSrc;
    slide.setting.mapImageOpacity = themeConfig.images?.mapImageOpacity;
    slide.setting.mapConfig = themeConfig.settings?.mapConfig;
  } else if (value === 'wheel') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.wheelHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.choiceCircleImageSrc = themeConfig.images?.choiceCircleImageSrc;
    slide.setting.choiceCircleImageOpacity = themeConfig.images?.choiceCircleImageOpacity;
  } else if (value === 'talkingTiles') {
    slide.setting.FontStyle = themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.talkingTilesHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.talkingTilesImageSrc = themeConfig.images?.talkingTilesImageSrc;
    slide.setting.talkingTilesImageOpacity = themeConfig.images?.talkingTilesImageOpacity;
  } else if (value === 'newWheel') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.newWheelHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.winningWheelImageSrc = themeConfig.images?.winningWheelImageSrc;
    slide.setting.winningWheelImageOpacity = themeConfig.images?.winningWheelImageOpacity;
  } else if (value === 'linkLibrary') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.linkLibraryHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.linkLibraryImageSrc = themeConfig.images?.linkLibraryImageSrc;
    slide.setting.linkLibraryImageOpacity = themeConfig.images?.linkLibraryImageOpacity;
  } else if (value === 'Quick Questions') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.quickQuestionsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.quickQuestionImageSrc = themeConfig.images?.quickQuestionImageSrc;
    slide.setting.quickQuestionImageOpacity = themeConfig.images?.quickQuestionImageOpacity;
  } else if (value === 'ratingPolls') {
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.ratingPollsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.ratingPollsImageSrc = themeConfig.images?.ratingPollsImageSrc;
    slide.setting.ratingPollsImageOpacity = themeConfig.images?.ratingPollsImageOpacity;
  } else if (value === 'multipleChoice') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.multipleChoiceFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.multipleChoiceHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.multipleChoiceImageSrc = themeConfig.images?.multipleChoiceImageSrc || themeConfig.default?.imageSrc;
    slide.setting.multipleChoiceImageOpacity = themeConfig.images?.multipleChoiceImageOpacity || themeConfig.default?.imageOpacity;
  } else if (value === 'quiz') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.quizFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.quizHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.quizImageSrc = themeConfig.images?.quizImageSrc || themeConfig.default?.imageSrc;
    slide.setting.quizImageOpacity = themeConfig.images?.quizImageOpacity || themeConfig.default?.imageOpacity;
  } else if (value === 'leaderboard') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.leaderboardFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.leaderboardHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.leaderboardImageSrc = themeConfig.images?.leaderboardImageSrc || themeConfig.default?.imageSrc;
    slide.setting.leaderboardImageOpacity = themeConfig.images?.leaderboardImageOpacity || themeConfig.default?.imageOpacity;
  } else if (value === 'credits') {
    slide.setting.FontStyle = themeConfig.textFontFamily?.creditsFontStyle || themeConfig.textFontFamily?.fantasticFansFontStyle;
    slide.setting.HeadingFont = themeConfig.headingFontFamily?.creditsHeadingFont || themeConfig.headingFontFamily?.fantasticFansHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
    slide.setting.creditsImageSrc = themeConfig.images?.creditsImageSrc || themeConfig.images?.ffImageSrc;
    slide.setting.creditsImageOpacity = themeConfig.images?.creditsImageSrc
      ? themeConfig.images?.creditsImageOpacity
      : themeConfig.images?.ffImageOpacity;
    if (!slide.color.creditsBgColor) slide.color.creditsBgColor = slide.color.ffBgColor || themeConfig.colors?.ffBgColor || themeConfig.default?.bgColor || '#111111';
    if (!slide.color.creditsTextColor) slide.color.creditsTextColor = slide.color.ffTextColor || themeConfig.colors?.ffTextColor || themeConfig.default?.headingColor || '#FFFFFF';
    if (!slide.color.creditsHeadingColor) slide.color.creditsHeadingColor = slide.color.ffHeadingColor || themeConfig.colors?.ffHeadingColor || themeConfig.default?.headingColor || '#FFFFFF';
  }
}
